'use client';

import {useRef, useState} from 'react';
import {Loader2, CheckCircle2, AlertCircle, X, Upload} from 'lucide-react';
import {ACCEPTED_EXTENSIONS, MAX_TAKEHOME_SUBMISSION_FILES} from 'iq-takehomes/config';
import type {TakeHomeFile} from 'iq-interview_experiences/types';

const MAX_FILE_BYTES = 25 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getExt(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

type Props = {
  files: TakeHomeFile[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  disabled?: boolean;
  acceptedExtensions?: Set<string>;
};

function FileRow({file, onRemove}: {file: TakeHomeFile; onRemove: () => void}) {
  return (
    <div className='flex items-center gap-3 py-2 px-3 rounded-lg bg-white border border-iq-grey-light'>
      <div className='shrink-0'>
        {file.uploading ? (
          <Loader2 className='w-4 h-4 text-iq-grey-medium animate-spin' />
        ) : file.error ? (
          <AlertCircle className='w-4 h-4 text-iq-red' />
        ) : (
          <CheckCircle2 className='w-4 h-4 text-iq-green' />
        )}
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm text-iq-black truncate'>{file.name}</p>
        {file.error ? (
          <p className='text-xs text-iq-red'>{file.error}</p>
        ) : (
          <p className='text-xs text-iq-grey-medium'>{formatBytes(file.size)}</p>
        )}
      </div>
      <button
        type='button'
        onClick={onRemove}
        className='shrink-0 text-iq-grey-medium hover:text-iq-black transition-colors'>
        <X className='w-4 h-4' />
      </button>
    </div>
  );
}

export function FlowTakehomeUpload({files, onAddFiles, onRemoveFile, disabled, acceptedExtensions = ACCEPTED_EXTENSIONS}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const acceptAttr = Array.from(acceptedExtensions).join(',');
  const isPdfOnly = acceptedExtensions.size === 1 && acceptedExtensions.has('.pdf');
  const fileHint = isPdfOnly
    ? 'PDF only · max 25 MB'
    : 'PDF, Word, Markdown, Notebook, ZIP, XLSX · max 25 MB';
  const isAtCapacity = files.length >= MAX_TAKEHOME_SUBMISSION_FILES;

  function validate(incoming: File[]): {valid: File[]; error: string | null} {
    if (files.length >= MAX_TAKEHOME_SUBMISSION_FILES) {
      return {valid: [], error: 'Remove the current file before uploading a new one.'};
    }
    const f = incoming[0];
    if (!f) return {valid: [], error: null};
    if (!acceptedExtensions.has(getExt(f.name))) {
      return {valid: [], error: `Unsupported file type: ${f.name}`};
    }
    if (f.size > MAX_FILE_BYTES) {
      return {valid: [], error: `${f.name} exceeds 25 MB`};
    }
    return {valid: [f], error: null};
  }

  function handleFiles(incoming: File[]) {
    const {valid, error} = validate(incoming);
    setRejectError(error);
    if (valid.length > 0) onAddFiles(valid);
  }

  return (
    <div className='flex flex-col gap-3'>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => !disabled && !isAtCapacity && inputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${
          dragOver
            ? 'border-iq-black bg-white'
            : 'border-iq-grey-light bg-white hover:border-iq-grey-medium'
        } ${disabled || isAtCapacity ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <Upload className='w-5 h-5 text-iq-grey-medium' />
        <p className='text-sm text-iq-grey-medium text-center'>
          Drag files here or <span className='text-iq-black font-medium'>click to browse</span>
        </p>
        <p className='text-xs text-iq-grey-medium text-center'>{fileHint}</p>
      </div>
      <input
        ref={inputRef}
        type='file'
        accept={acceptAttr}
        className='hidden'
        disabled={disabled || isAtCapacity}
        onChange={(e) => {
          handleFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      {rejectError && (
        <p className='text-xs text-iq-red'>{rejectError}</p>
      )}
      {files.length > 0 && (
        <div className='flex flex-col gap-1.5'>
          {files.map((f) => (
            <FileRow key={f.id} file={f} onRemove={() => onRemoveFile(f.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
